import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../../hooks/useAuth';
import { useLeadForm } from '../hooks/useLeadForm';
import { useLeadAutoSave } from '../hooks/useLeadAutoSave';
import LeadEditForm from '../LeadEditForm';

const InitialDealDialog = ({ open, lead, onClose, onUpdated, onSubmitForReview }) => {
  const { user } = useAuth();
  const leadId = lead?.id;
  const userRoleName = (user?.roles?.[0]?.name || user?.role?.name || '').toLowerCase();
  const currentUserId = user?.id ?? user?.uuid;

  const form = useLeadForm({
    leadId,
    isCreateMode: false,
    user,
    enabled: open && Boolean(leadId),
    prefillLead: lead,
    useInitiateEndpoint: true,
  });

  const { autoSaving, autoSaved, resetAutoSave } = useLeadAutoSave({
    enabled: open && Boolean(leadId),
    leadId,
    formData: form.formData,
    statusOptions: form.statusOptions,
    loading: form.loading,
    leadLoading: form.leadLoading,
    attachmentsHydrate: form.attachmentsHydrate,
    extraPayload: { initiate_deal: true },
  });

  // Force assignee_ids to current user whenever form data is hydrated
  useEffect(() => {
    if (!open || !currentUserId) return;
    form.setFormData((prev) => ({ ...prev, assignee_ids: [currentUserId] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.attachmentsHydrate, currentUserId]);

  // Hide Live Transfer status for closer role
  const filteredStatusOptions = userRoleName === 'closer'
    ? form.statusOptions.filter((s) => (s.name || '').toLowerCase().trim() !== 'live transfer')
    : form.statusOptions;

  const hasPrefill = Boolean(form.formData.name || form.formData.dba_name);
  const showBlockingLoader = form.loading && !hasPrefill;

  const handleClose = () => {
    if (autoSaving) return;
    resetAutoSave();
    form.resetForm();
    onUpdated?.();
    onClose();
  };

  const handleSaveDraft = () => {
    if (autoSaving) return;
    handleClose();
  };

  const handleSubmitted = () => {
    if (autoSaving) return;
    resetAutoSave();
    form.resetForm();
    onSubmitForReview?.(lead);
  };

  const title = lead?.name || lead?.dba_name || 'Lead';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ pr: 10 }}>
        Initiate Deal — {title}
        <Box
          sx={{
            position: 'absolute',
            right: 48,
            top: 14,
            display: 'flex',
            alignItems: 'center',
            minWidth: 24,
            minHeight: 24,
          }}
        >
          {autoSaving ? (
            <CircularProgress size={18} thickness={5} />
          ) : autoSaved ? (
            <Tooltip title="Auto-saved">
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} />
            </Tooltip>
          ) : null}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={autoSaving}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {showBlockingLoader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Changes are saved automatically when you update any field.
            </Typography>
            <LeadEditForm
              {...form}
              statusOptions={filteredStatusOptions}
              isCreateMode={false}
              leadId={leadId}
              user={user}
              isDisabled={form.loading || form.leadLoading}
              hideStatus
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={autoSaving}>
          Close
        </Button>
        <Button onClick={handleSaveDraft} disabled={autoSaving} variant="outlined">
          Save Draft
        </Button>
        <Button onClick={handleSubmitted} disabled={autoSaving} variant="contained">
          Submitted
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InitialDealDialog;
